import { chunkRng, clamp, hash01, hash3, lerp, mixRgb, smoothstep, valueNoise } from '@/core/rng';
import { CHUNK_SIZE, type ChunkData, type Instance } from './chunk';
import { SCAR_CELL } from './overlay';
import type { EnvFieldSnapshot } from '@/sim/persistence/snapshot';

/**
 * Sparse environmental state on the disturbance lattice — the land remembers.
 *
 * Each touched cell holds *deviations* from the pure baseline: moisture and
 * biomass, in [-1, 1], decaying exponentially toward zero on the world clock.
 * Rain and Strengthen push up, Weaken pushes down; a single act fades, repetition
 * sustains. Nature, gods and (later) mortals all write these same fields —
 * nobody gets a private ecology.
 *
 * The baseline underneath is never touched. Rebuild a chunk with an empty
 * field and the land is exactly what the seed says it is.
 */

export interface EnvInfo { moisture: number; biomass: number }

interface EnvCell {
  gx: number; gz: number;
  moisture: number; biomass: number;   // deviation as of time `at`
  at: number;
  /**
   * How long this cell's deviation lasts, as a multiple of the base half-life
   * — `pm` for moisture, `pb` for biomass. 1 (or absent) is the ordinary land.
   *
   * ## The ground remembers how durable the hand was, never whose it was
   *
   * This is the only shape a per-god persistence can take here, and the reason
   * is law 10 rather than convenience. The obvious store is a `godId` on the
   * cell — but `applyEnv` reads this field to tint chunks, so a hand written
   * into it is a hand that has reached something witnessable. A number saying
   * *this growth is stubborn* carries no cause at all: to every eye in the
   * world the valley is simply slower to forget, which is exactly the gap
   * religion grows in.
   *
   * It also solves the problem a `godId` could not. Cells are SUMMED — two
   * gods, or one god twice, deviate the same ground — and "whose cell is it"
   * has no answer. "How stubborn is what is here" always does: on each stamp
   * the persistence blends toward the new hand's, weighted by how much of the
   * cell's magnitude that hand just contributed.
   */
  pm?: number; pb?: number;
}

/** Blend a cell's persistence toward a new hand's, by how much it contributed. */
const blend = (was: number, had: number, adding: number, next: number): number => {
  const a = Math.abs(had), b = Math.abs(adding);
  return a + b <= 1e-12 ? was : (a * was + b * next) / (a + b);
};

// half-lives in world-seconds: wet ground dries in days, growth lingers weeks
const MOISTURE_HALF = 5 * 86400;
const BIOMASS_HALF = 18 * 86400;
// below this a deviation is noise, and the cell is forgotten
const NEGLIGIBLE = 0.02;

const CELLS_PER_CHUNK = CHUNK_SIZE / SCAR_CELL;
const cellChunk = (gx: number, gz: number) =>
  `${Math.floor(gx / CELLS_PER_CHUNK)},${Math.floor(gz / CELLS_PER_CHUNK)}`;

const decay = (v: number, dt: number, half: number) => v * Math.pow(0.5, dt / half);

export class EnvField {
  private readonly cells = new Map<string, EnvCell>();
  private version_ = 0;

  /**
   * Stamp a verb onto the land: a soft disc of deviation centred on (wx, wz).
   * Existing deviation decays to `at` first, then accumulates — so repeated
   * rain deepens wetness where one shower would fade. Returns the chunk keys
   * whose appearance changed.
   */
  stamp(
    wx: number, wz: number, radius: number, d: Partial<EnvInfo>, at: number,
    /**
     * How stubborn this hand's work is, as a multiple of the base half-life.
     * 1 is an ordinary god. Defaults to 1, so every existing caller — and the
     * whole world before anybody invests in it — is untouched.
     */
    persist: { moisture?: number; biomass?: number } = {},
  ): string[] {
    const dirty = new Set<string>();
    const g0x = Math.floor((wx - radius) / SCAR_CELL), g1x = Math.floor((wx + radius) / SCAR_CELL);
    const g0z = Math.floor((wz - radius) / SCAR_CELL), g1z = Math.floor((wz + radius) / SCAR_CELL);
    for (let gz = g0z; gz <= g1z; gz++) for (let gx = g0x; gx <= g1x; gx++) {
      const cx = (gx + 0.5) * SCAR_CELL, cz = (gz + 0.5) * SCAR_CELL;
      const fall = 1 - smoothstep(0.35, 1, Math.hypot(cx - wx, cz - wz) / radius);
      if (fall <= 0) continue;
      const key = `${gx},${gz}`;
      const prev = this.cells.get(key);
      const dt = prev ? at - prev.at : 0;
      const pm = prev?.pm ?? 1, pb = prev?.pb ?? 1;
      const m = prev ? decay(prev.moisture, dt, MOISTURE_HALF * pm) : 0;
      const b = prev ? decay(prev.biomass, dt, BIOMASS_HALF * pb) : 0;
      const dm = (d.moisture ?? 0) * fall, db = (d.biomass ?? 0) * fall;
      this.cells.set(key, {
        gx, gz,
        moisture: clamp(m + dm, -1, 1),
        biomass: clamp(b + db, -1, 1),
        at,
        pm: blend(pm, m, dm, persist.moisture ?? 1),
        pb: blend(pb, b, db, persist.biomass ?? 1),
      });
      dirty.add(cellChunk(gx, gz));
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** The deviation under a world position, decayed to now. Null where untouched. */
  at(wx: number, wz: number, now: number): EnvInfo | null {
    const c = this.cells.get(`${Math.floor(wx / SCAR_CELL)},${Math.floor(wz / SCAR_CELL)}`);
    if (!c) return null;
    const dt = now - c.at;
    const moisture = decay(c.moisture, dt, MOISTURE_HALF * (c.pm ?? 1));
    const biomass = decay(c.biomass, dt, BIOMASS_HALF * (c.pb ?? 1));
    if (Math.abs(moisture) < NEGLIGIBLE && Math.abs(biomass) < NEGLIGIBLE) return null;
    return { moisture, biomass };
  }

  /**
   * One cell by lattice index, decayed to now — the bracketing sample `envAt`
   * needs. Deliberately not folded into `at`, whose per-cell contract the
   * simulation and `test/focus.ts` both depend on.
   */
  cell(gx: number, gz: number, now: number): EnvInfo | null {
    const c = this.cells.get(`${gx},${gz}`);
    if (!c) return null;
    const dt = now - c.at;
    return {
      moisture: decay(c.moisture, dt, MOISTURE_HALF * (c.pm ?? 1)),
      biomass: decay(c.biomass, dt, BIOMASS_HALF * (c.pb ?? 1)),
    };
  }

  /**
   * The best-remembered patch of *growth* within `radius`, or null.
   *
   * Exists so mortals can walk to ground a god improved. `partyTarget` picks a
   * destination from `socialNeighbors`, which deliberately excludes the home
   * cell — and 18 of the shipped island's 27 bands sit within a few metres of
   * their own social-cell centre, so a cast at the altitude the game is played
   * at lands wholly inside the one cell a forage party can never be sent to.
   * Bless the camp you are watching and nobody comes.
   *
   * Only the sparse touched cells are scanned, so this costs nothing in a
   * world with no god in it and scales with how much a god has actually done —
   * which is the right coupling. Nothing but Strengthen writes a positive
   * biomass deviation (wear, trampling and fire all write negative), so in an
   * ungodded world this returns null everywhere and the economy is
   * bit-for-bit what it was.
   *
   * Ties break on cell coordinates rather than on Map order, so the answer
   * cannot depend on the sequence a field happened to be built in.
   */
  bestGrowth(wx: number, wz: number, radius: number, now: number):
    { x: number; z: number; biomass: number } | null {
    let bx = 0, bz = 0, bgx = 0, bgz = 0, top = 0;
    let found = false;
    for (const c of this.cells.values()) {
      const cx = (c.gx + 0.5) * SCAR_CELL, cz = (c.gz + 0.5) * SCAR_CELL;
      if (Math.hypot(cx - wx, cz - wz) > radius) continue;
      const info = this.at(cx, cz, now);
      if (!info || info.biomass <= 0) continue;
      const better = info.biomass > top
        || (info.biomass === top && (c.gx < bgx || (c.gx === bgx && c.gz < bgz)));
      if (!found || better) {
        found = true; top = info.biomass; bx = cx; bz = cz; bgx = c.gx; bgz = c.gz;
      }
    }
    return found ? { x: bx, z: bz, biomass: top } : null;
  }

  /** Forget cells that have decayed to nothing; return their chunk keys. */
  sweep(now: number): string[] {
    const dirty = new Set<string>();
    for (const [key, c] of this.cells) {
      const dt = now - c.at;
      if (Math.abs(decay(c.moisture, dt, MOISTURE_HALF * (c.pm ?? 1))) < NEGLIGIBLE &&
          Math.abs(decay(c.biomass, dt, BIOMASS_HALF * (c.pb ?? 1))) < NEGLIGIBLE) {
        this.cells.delete(key);
        dirty.add(cellChunk(c.gx, c.gz));
      }
    }
    if (dirty.size > 0) this.version_++;
    return [...dirty];
  }

  /** All chunk keys currently carrying deviation. */
  chunks(): string[] {
    const keys = new Set<string>();
    for (const c of this.cells.values()) keys.add(cellChunk(c.gx, c.gz));
    return [...keys];
  }

  get size(): number { return this.cells.size; }

  /**
   * Bumps whenever the field's contents change. Not history — a cache tag:
   * day-canonical readers key on it so a mid-day stamp invalidates any
   * reading pinned earlier that day, in every world that replays this one.
   */
  get version(): number { return this.version_; }

  /** Every touched cell, copied out in insertion order (spec §49). */
  toSnapshot(): EnvFieldSnapshot {
    return { cells: [...this.cells.values()].map((c) => ({ ...c })) };
  }

  /** Rebuild a field from a snapshot. Shares nothing with the source. */
  static fromSnapshot(snap: EnvFieldSnapshot): EnvField {
    const field = new EnvField();
    for (const c of snap.cells) field.cells.set(`${c.gx},${c.gz}`, { ...c });
    return field;
  }
}

/**
 * The most a Strengthen may push the GROUND toward lush, whatever it does to
 * the plants standing on it. 0.45 unclamped read as half a camp mark from
 * three kilometres; 0.16 leaves ground that is visibly doing better without
 * the disc being a painted slab. Measured by the tint pass in tools/quicken.ts.
 */
const LUSH_CAP = 0.16;

const DAMP = [0.55, 0.62, 0.68] as const;      // multipliers: wet ground darkens, cools
const LUSH = [0.30, 0.47, 0.24] as const;      // strengthen pulls toward saturated growth
const STRAW = [0.56, 0.48, 0.30] as const;     // weaken pulls toward standing dead
const DUST = [0.72, 0.67, 0.55] as const;      // drought bleaches

// what a strengthen grows (0..1 colours): green-gold grass, dark low shrubs,
// and the occasional fleck of flower colour scattered through the sward
const GRASS_GREEN = [0.30, 0.44, 0.17] as const;
const GRASS_GOLD = [0.57, 0.51, 0.25] as const;
const SHRUB_GREEN = [0.24, 0.35, 0.16] as const;
const FLECKS = [
  [0.80, 0.64, 0.32],   // soft ochre
  [0.88, 0.86, 0.79],   // white
  [0.56, 0.43, 0.70],   // soft purple
] as const;
// fruit on a bush that is doing well: deep red and blue-black, the two colours
// a berry actually is. Kept dark, because a bright one reads as a flower.
const BERRY = [
  [0.42, 0.10, 0.12],
  [0.20, 0.13, 0.30],
] as const;

// applyEnv has no region and needs none: cover *positions* come from a fixed
// seed-like salt, and whether a position materialises is the env's decision
const COVER_SEED = 0xb100;
const COVER_SALT = 9;                 // distinct stream from buildVegetation's 7
/**
 * Added instances per chunk at lod 0.
 *
 * Was 90, and `tools/quicken.ts` priced what that bought: on a 540-plant
 * riparian chunk a cast at FULL biomass — the very most a god can ever write
 * into the land — added 89 things, **+16.5%**, each of them 0.85 m tall and
 * worth 3.6 px at the altitude the screenshot gate is run at, beside a
 * baseline plant's 17.8. Verified live on the same day: a 300 m Strengthen
 * that saturated the disc left the frame pixel-identical.
 *
 * A sixth more grass is not a thing anyone sees. This is the number that makes
 * a blessed hillside read as a thicker hillside.
 */
const MAX_COVER = 300;
// Enough clumps that the candidate pool actually REACHES MAX_COVER. The
// materialisation threshold falls across the index range, so a pool shorter
// than the range never exposes its low-threshold tail — raising MAX_COVER to
// 300 without this silently killed weak blessings, which grew 20 things at a
// 0.30 deviation before and 0 after.
const COVER_CLUMPS = 52;
// materialisation threshold falls with candidate index across this range, so
// rising biomass fills the hillside in: 0.2 keeps a few, 0.5 many, 0.8 dense
const COVER_T_HI = 0.9, COVER_T_LO = 0.12;

/** The chunk's own idea of the ground: bilinear over its height grid. */
export function chunkHeightAt(data: ChunkData, x: number, z: number): number {
  const step = CHUNK_SIZE / (data.res - 1);
  const fx = clamp((x - data.cx * CHUNK_SIZE) / step, 0, data.res - 1);
  const fz = clamp((z - data.cz * CHUNK_SIZE) / step, 0, data.res - 1);
  const i0 = Math.min(Math.floor(fx), data.res - 2);
  const j0 = Math.min(Math.floor(fz), data.res - 2);
  const tx = fx - i0, tz = fz - j0;
  const h00 = data.height[j0 * data.res + i0], h10 = data.height[j0 * data.res + i0 + 1];
  const h01 = data.height[(j0 + 1) * data.res + i0], h11 = data.height[(j0 + 1) * data.res + i0 + 1];
  return lerp(lerp(h00, h10, tx), lerp(h01, h11, tx), tz);
}

/**
 * The responsive ground layer a Strengthen grows: grasses, flower flecks and low
 * shrubs, generated per chunk from a fixed rng stream. Every candidate is
 * drawn identically at every LOD and on every call; a candidate materialises
 * only where the land's *current* biomass deviation clears its threshold.
 * Same env state + same chunk ⇒ identical instances, in any call order —
 * and as the strengthen decays or weakens, the cover thins back out on rebuild.
 */
function bloomCover(data: ChunkData, env: EnvField, now: number): Instance[] {
  if (data.lod > 2) return [];
  // coarser rungs keep an even sample of the same candidates, never new ones
  const stride = data.lod === 0 ? 1 : data.lod === 1 ? 2 : 4;
  const cap = Math.floor(MAX_COVER / stride);
  const rnd = chunkRng(COVER_SEED, data.cx, data.cz, COVER_SALT);
  const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;
  const out: Instance[] = [];
  let idx = 0;

  for (let c = 0; c < COVER_CLUMPS; c++) {
    const px = ox + rnd() * CHUNK_SIZE, pz = oz + rnd() * CHUNK_SIZE;
    const spread = 2.5 + rnd() * 4.5;
    const many = 5 + Math.floor(rnd() * 5);
    for (let k = 0; k < many; k++) {
      const a = rnd() * Math.PI * 2, r = Math.sqrt(rnd()) * spread;
      const x = px + Math.cos(a) * r, z = pz + Math.sin(a) * r;
      const grow = rnd();          // size within the tuft
      const tone = rnd();          // green .. gold
      const jitter = rnd();        // per-blade shade
      const rot = rnd() * Math.PI * 2;
      const flower = rnd();        // occasional fleck
      const shrubby = rnd();       // low-shrub chance at the top end
      // filter only after the whole rng draw, so every LOD stays in step
      const i = idx++;
      if (i % stride !== 0 || out.length >= cap) continue;
      const need = COVER_T_LO
        + (COVER_T_HI - COVER_T_LO) * clamp(1 - i / (MAX_COVER - 1), 0, 1);
      // `envAt`, not `at`: a blessing's NEW plants were materialised by
      // comparing a per-candidate threshold against a per-cell step, so the
      // grass a Strengthen grew came in as a square patch whose density
      // jumped at the cell border. This is the third place the lattice was
      // visible and the most damning, because these plants are the cast's
      // whole visible answer.
      const e = envAt(env, x, z, now);
      if (!e || e.biomass <= need) continue;
      const y0 = chunkHeightAt(data, x, z);
      if (y0 <= 1.0) continue;     // no site data here: height is the land test
      const b = clamp(e.biomass, 0, 1);
      // Bushes, and more of them the better the land is doing. `shrubby < 0.22`
      // at biomass > 0.55 gave a stand that was 70% grass at every strength a
      // god could reach; what a tended slope should look like is scrub coming
      // in, so the share of woody cover rises with the blessing.
      const shrub = b > 0.4 && shrubby < 0.18 + b * 0.34;
      // and they STAND UP. The old rule capped everything at ~1.36 m — the
      // comment called it "always understory", which was the intent and also
      // exactly why a cast was invisible: 0.85 m is 3.6 px from 250 m against
      // a baseline plant's 17.8. A blessed shrub now reaches ~2.6 m, which is
      // scrub rather than sward, and still nothing like the 25 m canopy: a
      // Strengthen thickens a hillside, it does not conjure a forest.
      const h = shrub ? (0.9 + grow * 0.55) * (1 + b * 0.85)
                      : (0.45 + grow * 0.65) * (1 + b * 0.35);
      let col = mixRgb(shrub ? SHRUB_GREEN : GRASS_GREEN, GRASS_GOLD, tone * (shrub ? 0.25 : 0.7));
      col = mixRgb(col, LUSH, b * 0.4);
      if (!shrub && flower < 0.16)
        col = mixRgb(col, FLECKS[hash3(Math.round(x * 5), Math.round(z * 5), 0xf1ec) % 3], 0.55);
      // and on well-tended ground the bushes come into berry. Griffin's own
      // description of what a verb should look like: significance shows as
      // living things DOING something, never as tinted dirt — which is why
      // promotion's ground mark is parked and this is not.
      if (shrub && b > 0.62 && flower < 0.34)
        col = mixRgb(col, BERRY[hash3(Math.round(x * 7), Math.round(z * 7), 0xbe22) % 2],
                     0.28 + (b - 0.62) * 0.5);
      const shade = 0.88 + jitter * 0.28;
      const sy = h * (0.85 + grow * 0.30);   // scaleY ~0.4–1.4: always understory
      out.push({
        x, z,
        y: y0,                                 // the geometry stands on its base
        scaleXZ: h * (shrub ? 1.4 + tone * 0.6 : 0.9 + tone * 0.5),
        scaleY: sy,
        rot,
        colour: [col[0] * shade, col[1] * shade, col[2] * shade],
        kind: shrub ? 'shrub' : 'tuft',
        // regrowth wears the first build; a recovering slope needs no wardrobe
        variant: 0,
      });
    }
  }
  return out;
}

/**
 * Stamp the environmental state onto freshly built chunk data, main thread.
 * Runs before the burn overlay, so a scar chars over a strengthen, not under it.
 */
/**
 * What the land's remembered condition does to the colour of the ground.
 *
 * Extracted from `applyEnv` so there is **one** source of truth for it: the
 * chunk transform calls it per vertex, and `world/farField.ts` calls it for
 * the horizon mesh, which until now read no history at all and so showed a
 * watershed-scale blessing or a burn as untouched wilderness. Two copies of
 * this arithmetic would have drifted the first time either was tuned.
 *
 * Pure, and the maths is unchanged from the version that was measured.
 */
/**
 * Metres per noise period and how far the edge may wander, matched to
 * `overlay.ts`'s burn edge so a blessing and a burn break up at the same
 * grain. A different salt, or the two would ripple in step wherever they meet.
 */
const ENV_EDGE_SCALE = 11, ENV_EDGE_SWING = 0.34;
const S_ENV_EDGE = 0x2f9d;

/**
 * The band over which a drought takes the understory. The old rule was a step
 * at 0.45; this brackets it, so the ground that used to be exactly at the
 * threshold still loses about half its small plants and the rest thin out on
 * either side instead of ending on a lattice line.
 */
const WITHER_LO = 0.28, WITHER_HI = 0.72;
const S_WITHER = 0x3ac5;

/**
 * WHAT THE EYE SEES OF A DIVINE ACT — the soft twin of `EnvField.at`.
 *
 * Same split, same reason as `overlay.ts`'s `burnAt`, and it matters more here
 * because this is the field the player writes to directly. `at()` stays a
 * per-cell step because the simulation reads it — `sim/social/habitat.ts:94`
 * scores ground with it, `sim/fire.ts:246` asks it whether ground is too wet
 * to burn, `sim/simulation.ts:1028` prices a cast against it — and because
 * `test/focus.ts` pins a designed property of exactly that shape: a cast
 * tighter than one cell marks its own cell and saturates it. Smoothing `at()`
 * would move all of them; smoothing what is *drawn* moves nothing but the
 * picture.
 *
 * The picture was the problem. `act()` snaps any cast under 32 m to the cell
 * centre and stamps a 16 m radius, and at 16 m the falloff reaches no
 * neighbouring centre at all — so a Strengthen at the altitude the game is
 * actually played at wrote exactly one cell at full strength, and the player
 * was shown a perfect 32 m square of greener ground with a step at its edge.
 * Interpolating and then roughening turns that same single cell into a soft,
 * irregular patch about the size of the cast, which is what a cast on a
 * hillside should look like — without moving one number the world reasons
 * over.
 */
export function envAt(
  env: EnvField, wx: number, wz: number, now: number,
): EnvInfo | null {
  const fx = wx / SCAR_CELL - 0.5, fz = wz / SCAR_CELL - 0.5;
  const i = Math.floor(fx), j = Math.floor(fz);
  const u = fx - i, v = fz - j;
  const w = [(1 - u) * (1 - v), u * (1 - v), (1 - u) * v, u * v];
  const at = [
    env.cell(i, j, now), env.cell(i + 1, j, now),
    env.cell(i, j + 1, now), env.cell(i + 1, j + 1, now),
  ];

  let moisture = 0, biomass = 0, any = false;
  for (let k = 0; k < 4; k++) {
    const c = at[k];
    if (!c) continue;                       // absent is zero deviation, not a gap
    moisture += w[k] * c.moisture;
    biomass += w[k] * c.biomass;
    any = true;
  }
  if (!any) return null;

  // The swing is signed-safe: it scales magnitude, so a withered cell stays
  // withered and a blessed one stays blessed — it only moves where the edge
  // falls. Applying it to the value rather than the sign is what keeps
  // `envTint`'s two branches from flickering across each other at the rim.
  const swing = lerp(1 - ENV_EDGE_SWING, 1 + ENV_EDGE_SWING,
                     valueNoise(wx / ENV_EDGE_SCALE, wz / ENV_EDGE_SCALE, S_ENV_EDGE));
  moisture = clamp(moisture * swing, -1, 1);
  biomass = clamp(biomass * swing, -1, 1);
  if (Math.abs(moisture) < NEGLIGIBLE && Math.abs(biomass) < NEGLIGIBLE) return null;
  return { moisture, biomass };
}

export function envTint(
  base: readonly [number, number, number], e: EnvInfo,
): [number, number, number] {
  let [r, g, b] = base;
  if (e.moisture > 0) {
    const w = clamp(e.moisture, 0, 1) * 0.5;
    r = lerp(r, r * DAMP[0], w); g = lerp(g, g * DAMP[1], w); b = lerp(b, b * DAMP[2], w);
  } else if (e.moisture < 0) {
    const w = clamp(-e.moisture, 0, 1) * 0.3;
    r = lerp(r, DUST[0], w); g = lerp(g, DUST[1], w); b = lerp(b, DUST[2], w);
  }
  if (e.biomass > 0) {
      // Capped, and the cap is a design decision rather than a taste one.
      //
      // Griffin's rule for what a divine act should look like is that
      // significance shows as LIVING THINGS DOING SOMETHING and never as
      // tinted dirt — it is why promotion's ground mark is parked. This branch
      // was the same carrier wearing different clothes: ungated at 0.45, and a
      // cast saturates biomass to 1.000, so a blessed disc moved the ground's
      // mean RGB by 0.089 against a camp's packed earth at 0.193 — a mark
      // ROADMAP §13 verified reads from three kilometres. Half a camp, painted
      // on, with no plant involved.
      //
      // Growth still darkens and greens the ground a little, because wet
      // fertile ground genuinely is darker and greener than dry. It just stops
      // being the thing you notice: `bloomCover` now puts 55% more plants on
      // that ground, standing up to 3 m, and they are what the eye should
      // catch. Weaken is untouched at 0.55 — a withered slope going straw IS
      // the ground, and there are no new plants there to carry it.
    const w = Math.min(clamp(e.biomass, 0, 1) * 0.45, LUSH_CAP);
    r = lerp(r, LUSH[0], w); g = lerp(g, LUSH[1], w); b = lerp(b, LUSH[2], w);
  } else if (e.biomass < 0) {
    const w = clamp(-e.biomass, 0, 1) * 0.55;
    r = lerp(r, STRAW[0], w); g = lerp(g, STRAW[1], w); b = lerp(b, STRAW[2], w);
  }
  return [r, g, b];
}

export function applyEnv(
  data: ChunkData,
  env: EnvField,
  now: number,
  /** Places a drought may not kill — see `world/things.ts`. */
  spare?: (x: number, z: number) => boolean,
): ChunkData {
  if (env.size === 0) return data;
  const step = CHUNK_SIZE / (data.res - 1);
  const ox = data.cx * CHUNK_SIZE, oz = data.cz * CHUNK_SIZE;

  for (let j = 0; j < data.res; j++) for (let i = 0; i < data.res; i++) {
    const e = envAt(env, ox + i * step, oz + j * step, now);
    if (!e) continue;
    const k = (j * data.res + i) * 3;
    const c = envTint([data.colour[k], data.colour[k + 1], data.colour[k + 2]], e);
    data.colour[k] = c[0]; data.colour[k + 1] = c[1]; data.colour[k + 2] = c[2];
  }

  // vegetation: withered ground loses its understory and browns its canopy;
  // blooming ground deepens every green. Baseline structure never changes here.
  const kept: Instance[] = [];
  for (const inst of data.vegetation) {
    const e = envAt(env, inst.x, inst.z, now);
    if (!e) { kept.push(inst); continue; }
    // The grasses died — except where one of them is a thing history kept.
    //
    // Dithered across a band for the same reason the burn is: a hard test at
    // −0.45 against a per-cell step killed every small plant inside the cell
    // and none outside it, so a Weaken drew a square of stripped understory.
    // The hash is of the plant's own position, so which plants a drought takes
    // is stable across LOD and rebuilds.
    const wither = -e.biomass;                 // positive where the ground is dying
    if (wither > WITHER_LO && inst.scaleY < 2.5 && !spare?.(inst.x, inst.z) &&
        hash01(Math.round(inst.x * 8), Math.round(inst.z * 8), S_WITHER)
          < smoothstep(WITHER_LO, WITHER_HI, wither)) continue;
    if (e.biomass < 0) {
      const w = clamp(-e.biomass, 0, 1) * 0.7;
      inst.colour = [
        lerp(inst.colour[0], 0.42, w),
        lerp(inst.colour[1], 0.33, w),
        lerp(inst.colour[2], 0.16, w),
      ];
    } else if (e.biomass > 0) {
      const w = clamp(e.biomass, 0, 1) * 0.5;
      const c = mixRgb([inst.colour[0], inst.colour[1], inst.colour[2]],
                       [LUSH[0], LUSH[1], LUSH[2]], w);
      inst.colour = [c[0], c[1], c[2]];
    }
    kept.push(inst);
  }

  // and where biomass runs positive, the strengthen grows visible abundance:
  // a responsive ground layer that fills in as the deviation deepens
  for (const inst of bloomCover(data, env, now)) kept.push(inst);

  data.vegetation = kept;
  return data;
}
